import * as THREE from "three";
import type { Vec3 } from "../types";

/**
 * Abstract base for all OpenPlans scene elements.
 *
 * Extends THREE.Object3D so every element participates in the scene graph
 * and is reachable via traverse(). Subclasses declare ogid and ogType,
 * enabling type-safe queries for drawings, views, and schedules:
 *
 *   scene.traverse(o => { if (o instanceof OPElement) ... });
 */
export abstract class OPElement extends THREE.Object3D {
  abstract ogid: string;
  abstract ogType: string;
}

/**
 * Implemented by every project-anchored element (walls, slabs, openings,
 * datums) to translate and rotate by mutating their point-defining state and
 * re-running setOPGeometry().
 *
 * Asset/instance elements (OpenElement, OPBase nodes) carry a `placement`
 * transform instead — see `Placement` in `../types`. They do NOT implement
 * `IPlaceable`; their movement is via the placement field.
 */
export interface IPlaceable {
  /** Translate this element by `delta` (world-space, meters). */
  move(delta: Vec3): void;
  /** Rotate this element by `angleDeg` around `axis` (default Y-up), pivoted
   *  at `anchor` (default the element's own centroid). */
  rotate(angleDeg: number, axis?: Vec3, anchor?: Vec3): void;
}

/**
 * Visual primitive stored in an element's subElements2D / subElements3D maps.
 *
 * Every kernel primitive (`Polygon`, `Solid`, `Sweep`, `Cuboid`, `Cylinder`,
 * etc.) declares an `outline` getter/setter independently. The maps also hold
 * `THREE.Group` containers, which do not have `outline` — hence `outline?` is
 * optional. Element classes iterate these maps and write `obj.outline = value`;
 * the assignment is a no-op for Groups and the real toggle for primitives.
 *
 * This interface is a local workaround for the kernel duplication tracked in
 * spec #38. Once `opengeometry` exports its own `OPGeometry` (kernel side),
 * this declaration goes away in favor of `import type { OPGeometry } from
 * "opengeometry"`.
 */
export interface OPGeometry extends THREE.Object3D {
  outline?: boolean;
}
